import { useEffect, useState } from "react";
import type { LessonStandardsSelection } from "./useLessonStandardsSelection";

type ScopeFilter = "all" | "selected";

export function LessonStandardsSection({ selection }: { selection: LessonStandardsSelection }) {
  const {
    loading,
    error,
    standardSets,
    selectedSetId,
    selectSet,
    standardsInSet,
    glossaryInSet,
    selectedStandardIds,
    selectedGlossaryIds,
    linkedStandards,
    linkedGlossary,
    toggleStandard,
    toggleGlossary,
  } = selection;
  const [filter, setFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter | null>(null);

  useEffect(() => {
    if (loading || scopeFilter !== null) return;
    const hasTags = linkedStandards.length > 0 || linkedGlossary.length > 0;
    setScopeFilter(hasTags ? "selected" : "all");
  }, [loading, scopeFilter, linkedStandards.length, linkedGlossary.length]);

  if (loading) return <p className="muted">Loading standards…</p>;

  if (standardSets.length === 0) {
    return (
      <div className="lesson-ws-section">
        <p className="muted">
          No published standard sets yet. An administrator can add them under Standards &amp; glossary.
        </p>
      </div>
    );
  }

  const scope = scopeFilter ?? "all";
  const scopedStandards =
    scope === "selected"
      ? standardsInSet.filter((s) => selectedStandardIds.has(s.id))
      : standardsInSet;
  const scopedGlossary =
    scope === "selected"
      ? glossaryInSet.filter((g) => selectedGlossaryIds.has(g.id))
      : glossaryInSet;

  const f = filter.trim().toLowerCase();
  const visibleStandards = f
    ? scopedStandards.filter(
        (s) => s.code.toLowerCase().includes(f) || s.description.toLowerCase().includes(f)
      )
    : scopedStandards;
  const visibleGlossary = f
    ? scopedGlossary.filter(
        (g) => g.term.toLowerCase().includes(f) || g.definition.toLowerCase().includes(f)
      )
    : scopedGlossary;

  return (
    <div className="lesson-ws-section">
      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      <p className="muted small">
        Tag this lesson with the standards and glossary terms it covers. Selections are saved with the lesson and used to
        guide AI generation.
      </p>

      <div className="field" style={{ maxWidth: "32rem" }}>
        <span className="field-label">Standard set</span>
        <select className="input" value={selectedSetId} onChange={(e) => selectSet(e.target.value)}>
          {standardSets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="lesson-standards-filters">
        <label className="field lesson-standards-filter">
          <span className="field-label">Show</span>
          <select
            className="input"
            value={scope}
            onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
          >
            <option value="selected">Selected only</option>
            <option value="all">All in set</option>
          </select>
        </label>
        <label className="field lesson-standards-filter">
          <span className="field-label">Search</span>
          <input
            className="input"
            value={filter}
            placeholder="Search standards and terms…"
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
      </div>

      <h3>
        Standards{" "}
        <span className="muted small">({selectedStandardIds.size} selected)</span>
      </h3>
      {visibleStandards.length === 0 ? (
        <p className="muted">
          {scope === "selected" ? "No selected standards in this set." : "No standards in this set."}
        </p>
      ) : (
        <ul className="lesson-standards-list">
          {visibleStandards.map((s) => {
            const checked = selectedStandardIds.has(s.id);
            return (
              <li key={s.id}>
                <label className={`lesson-standard-toggle${checked ? " is-active" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleStandard(s.id)} />
                  <span>
                    <strong>{s.code}</strong>
                    {s.category ? <span className="muted small"> · {s.category}</span> : null}
                    <span className="lesson-standard-desc">{s.description}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <h3>
        Glossary terms{" "}
        <span className="muted small">({selectedGlossaryIds.size} selected)</span>
      </h3>
      {visibleGlossary.length === 0 ? (
        <p className="muted">
          {scope === "selected" ? "No selected glossary terms in this set." : "No glossary terms in this set."}
        </p>
      ) : (
        <ul className="lesson-standards-list">
          {visibleGlossary.map((g) => {
            const checked = selectedGlossaryIds.has(g.id);
            return (
              <li key={g.id}>
                <label className={`lesson-standard-toggle${checked ? " is-active" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleGlossary(g.id)} />
                  <span>
                    <strong>{g.term}</strong>
                    <span className="lesson-standard-desc">{g.definition}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
